/**
 * In-app help (Stage 7 Phase 2A) — static, permission-aware. Each topic names
 * a permission; the panel shows only what the signed-in role can open.
 * Plain statements of what each page does — no compliance claims.
 */
export interface HelpTopic {
  title: string;
  permission: string | null; // null = everyone
  body: string;
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    title: 'Dashboard',
    permission: null,
    body: 'A summary of today: who is clocked in, open exceptions, pending leave, and payroll status for your scope.',
  },
  {
    title: 'Attendance',
    permission: 'attendance.view',
    body: 'Punches from your devices become daily records after attendance is processed. Exceptions (late, missing punch, duplicate) need review; use "Process attendance" to recompute a date range. Rules (grace, rounding, overtime) are in Settings → Attendance rules.',
  },
  {
    title: 'Employees and devices',
    permission: 'employee.view',
    body: 'An employee is linked to a device user ID so punches resolve to them. Fingerprints and faces are enrolled on the device itself and the system stores them centrally, encrypted. Using the employee number as the device user ID links them automatically.',
  },
  {
    title: 'Devices',
    permission: 'device.view',
    body: 'A device shows online when it was heard from recently, stale after a short gap, and offline after a longer one. "Last seen from" is the address the server saw, which is the site’s public IP, not the device’s LAN address.',
  },
  {
    title: 'Leave',
    permission: 'leave.view',
    body: 'Requests are approved or rejected against the employee’s balance. An approver cannot approve their own request.',
  },
  {
    title: 'Payroll',
    permission: 'payroll.view',
    body: 'A run is calculated, reviewed, approved and finalised in separate steps, each by a permitted role. Statutory deductions use the rule versions in force for the period.',
  },
  {
    title: 'Reports',
    permission: 'report.view',
    body: 'Reports can be exported as CSV. Exports are logged.',
  },
  {
    title: 'Need more help?',
    permission: null,
    body: 'Ask your company administrator, or contact your HR Clock provider with the page name and what you expected to see.',
  },
];

export function visibleHelp(can: (permission: string) => boolean): HelpTopic[] {
  return HELP_TOPICS.filter((t) => t.permission === null || can(t.permission));
}
